<script setup lang="ts">
  import { useTenant } from '@/shared/lib/tenant'

  definePage({
    meta: {
      layout: 'privcay',
    },
  })

  const { tenantName } = useTenant()
</script>

<template>
  <main class="prose">
    <article>
      <header>
        <h1>Privacy Policy</h1>
        <p>Privacy Policy for the {{ tenantName }} web application</p>
      </header>

      <nav>
        <h2>Table of Contents</h2>
        <ol>
          <li><a href="#information">Information</a></li>
          <li><a href="#introduction">1. Introduction</a></li>
          <li><a href="#data-categories">2. Categories of Personal Data We Collect</a></li>
          <li><a href="#data-sources">3. Sources of Data</a></li>
          <li><a href="#processing-purposes">4. Purposes of Processing</a></li>
          <li><a href="#legal-bases">5. Legal Bases for Processing</a></li>
          <li><a href="#third-party-sharing">6. Sharing Personal Data with Third Parties</a></li>
          <li><a href="#data-retention">7. Data Retention</a></li>
          <li><a href="#data-security">8. Data Security</a></li>
          <li><a href="#user-rights">9. Your Rights</a></li>
          <li><a href="#geolocation">10. Geolocation and Push Notifications</a></li>
          <li><a href="#children-privacy">11. Children's Privacy</a></li>
          <li><a href="#international-transfers">12. International Data Transfers</a></li>
          <li><a href="#policy-changes">13. Changes to this Policy</a></li>
          <li><a href="#contact">14. Contact</a></li>
        </ol>
      </nav>

      <section id="information">
        <h2>Information</h2>
        <ul>
          <li><b>Effective Date:</b> 10.04.2025</li>
          <li><b>Developer:</b> {{ tenantName }}</li>
          <li><b>Legal Address:</b> PO Box 701, San Mateo, California, 94401, US</li>
          <li><b>Contact for privacy matters:</b> <a href="mailto:privacy@aixment.com">privacy@aixment.com</a></li>
        </ul>
      </section>

      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy describes how {{ tenantName }} processes the personal data of users of the
          {{ tenantName }} web application. We follow the principles of lawfulness, fairness, transparency, and data
          minimization as per applicable legislation, including the General Data Protection Regulation (GDPR).
        </p>
      </section>

      <section id="data-categories">
        <h2>2. Categories of Personal Data We Collect</h2>
        <p>
          Depending on the user's role and activity in the app, we may collect and process the following categories of
          data:
        </p>

        <section>
          <h3>For {{ tenantName }} users:</h3>
          <ul>
            <li><strong>Contact information:</strong> name, email, phone number.</li>
            <li><strong>Authentication data:</strong> login credentials, OAuth tokens (e.g., via Reddy).</li>
            <li><strong>Profile information:</strong> position, employment status, family access status.</li>
            <li><strong>User activity:</strong> QR code usage, bookings, purchases, preferences.</li>
            <li><strong>Location data:</strong> if geolocation is enabled.</li>
            <li><strong>Notifications:</strong> push interaction history, likes/dislikes.</li>
            <li><strong>Device information:</strong> IP address, device type, ID, settings.</li>
          </ul>
        </section>
      </section>

      <section id="data-sources">
        <h2>3. Sources of Data</h2>
        <p>We may collect personal data:</p>
        <ul>
          <li>Directly from you during registration or use of the app;</li>
          <li>Automatically via technical means when you interact with the apps;</li>
          <li>From your employer (if you are a company employee);</li>
          <li>From partners (e.g., to confirm QR usage).</li>
        </ul>
      </section>

      <section id="processing-purposes">
        <h2>4. Purposes of Processing</h2>
        <p>We process your personal data to:</p>
        <ul>
          <li>Provide access to application features;</li>
          <li>Authenticate users and ensure security;</li>
          <li>Manage the loyalty program;</li>
          <li>Personalize content and offers;</li>
          <li>Deliver push notifications;</li>
          <li>Conduct analytics and improve the service;</li>
          <li>Fulfill legal obligations (e.g., GDPR compliance).</li>
        </ul>
      </section>

      <section id="legal-bases">
        <h2>5. Legal Bases for Processing</h2>
        <p>We rely on the following legal bases:</p>
        <ul>
          <li>Contract performance;</li>
          <li>Compliance with legal obligations;</li>
          <li>Our legitimate interests (e.g., fraud prevention, analytics);</li>
          <li>Your consent (for marketing, location services, notifications).</li>
        </ul>
      </section>

      <section id="third-party-sharing">
        <h2>6. Sharing Personal Data with Third Parties</h2>
        <p>We may share your data with:</p>
        <ul>
          <li>Service providers (hosting, analytics, messaging);</li>
          <li>Loyalty program partners (to verify QR redemption, for example);</li>
          <li>Analytics systems (e.g., Firebase, Google Analytics);</li>
          <li>Government bodies (when legally required);</li>
          <li>Business successors in the event of mergers or acquisitions.</li>
        </ul>
      </section>

      <section id="data-retention">
        <h2>7. Data Retention</h2>
        <p>We retain your data for as long as needed to fulfill the stated purposes or as required by law.</p>
      </section>

      <section id="data-security">
        <h2>8. Data Security</h2>
        <p>We implement physical, technical, and organizational safeguards, including:</p>
        <ul>
          <li>Data encryption;</li>
          <li>Authentication and role-based access;</li>
          <li>Activity logging and audits;</li>
          <li>Protection against token or QR code reuse.</li>
        </ul>
      </section>

      <section id="user-rights">
        <h2>9. Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access your personal data;</li>
          <li>Request corrections or deletion;</li>
          <li>Object to or restrict processing;</li>
          <li>Withdraw previously given consent;</li>
          <li>Submit a complaint to the data protection authority.</li>
        </ul>
      </section>

      <section id="geolocation">
        <h2>10. Geolocation and Push Notifications</h2>
        <p>If you consent, we may:</p>
        <ul>
          <li>Use your location to show nearby partner offers;</li>
          <li>Send you push notifications about offers, news, or reminders.</li>
        </ul>
        <p><em>You can change these permissions anytime in your device settings.</em></p>
      </section>

      <section id="children-privacy">
        <h2>11. Children's Privacy</h2>
        <p>
          The {{ tenantName }} app are not intended for users under 16. If we learn that data was collected from a child
          without appropriate consent, we will delete that information.
        </p>
      </section>

      <section id="international-transfers">
        <h2>12. International Data Transfers</h2>
        <p>
          Your data may be transferred outside your country of residence. In such cases, we ensure appropriate
          safeguards are in place, such as standard contractual clauses or equivalent protections.
        </p>
      </section>

      <section id="policy-changes">
        <h2>13. Changes to this Policy</h2>
        <p>
          We may update this policy. If the changes are material, we will notify users through the app or by other
          available means.
        </p>
      </section>

      <section id="contact">
        <h2>14. Contact</h2>
        <p>If you have questions or wish to exercise your rights, contact us:</p>
        <address>
          <div style="margin-bottom: 4px">
            <strong>Email:</strong> <a href="mailto:privacy@aixment.com">privacy@aixment.com</a>
          </div>
          <div><strong>Address:</strong> PO Box 701, San Mateo, California, 94401, US</div>
        </address>
      </section>
    </article>
  </main>

  <footer class="privacy-footer">
    <p>
      &copy; <span id="year">{{ new Date().getFullYear() }}</span> {{ tenantName }}. All rights reserved.
    </p>
  </footer>
</template>

<style scoped lang="scss">
  :global(body) {
    font-family: 'Montserrat', sans-serif;
    background: var(--background-bg-page);
    margin: 0;
    padding: 0;
  }
  main.prose {
    max-width: 1024px;
    width: 100%;
    margin: 0 auto;
    padding: 24px 16px;
    font-size: 16px;
    line-height: 1.5;
    background: var(--background-bg-page);
    color: var(--text-primary);
  }

  h1 {
    font-size: 36px;
    font-weight: 700;
    margin-top: 12px;
    margin-bottom: 16px;
  }

  h2 {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 16px;
    margin-top: 20px;
  }

  h3 {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  p {
    margin: 8px 0;
    line-height: 26px;
  }

  nav {
    padding: 16px 0;
    margin-bottom: 16px;
  }

  a {
    font-weight: 500;
    color: var(--link-default);
    text-decoration: none;
  }

  a:hover {
    color: var(--link-hover);
  }

  ol,
  ul {
    padding-left: 20px;
    padding-bottom: 12px;
  }

  ol li,
  ul li {
    padding: 4px 0;
    margin: 0;
    list-style: none;
  }

  section {
    padding: 24px;
    border: 1px solid var(--border-border-secondary);
    background: var(--background-bg-page);
    border-radius: 20px;
    margin-bottom: 16px;
  }

  section section {
    margin-top: 16px;
    padding: 0;
    border: none;
    border-radius: 0;
  }

  footer.privacy-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    font-size: 14px;
    color: var(--text-secondary);
  }
</style>
